import React, { useState, useMemo } from 'react'
import { X, Search, UserMinus, AlertTriangle } from 'lucide-react'
import { OrgChartService, RoleWithMember, TeamMember } from '../../services/orgChartService'
import { PersonCard } from './PersonCard'

interface AssignPersonModalProps {
  isOpen: boolean
  onClose: () => void
  onAssign: () => Promise<void> | void
  role: RoleWithMember
  teamMembers: TeamMember[]
  roles: RoleWithMember[]
}

export const AssignPersonModal: React.FC<AssignPersonModalProps> = ({
  isOpen,
  onClose,
  onAssign,
  role,
  teamMembers,
  roles
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const orgChartService = OrgChartService.getInstance()

  const assignableMembers = useMemo(
    () => teamMembers.filter(m => !m.hidden),
    [teamMembers]
  )

  // Build map of member -> roles they already hold
  const memberRolesMap = useMemo(() => {
    const map = new Map<string, RoleWithMember[]>()
    for (const r of roles) {
      if (r.assignedMemberId) {
        if (!map.has(r.assignedMemberId)) {
          map.set(r.assignedMemberId, [])
        }
        map.get(r.assignedMemberId)!.push(r)
      }
    }
    return map
  }, [roles])

  // Filter members by search
  const filteredMembers = useMemo(() => {
    let result = assignableMembers

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      result = result.filter(member =>
        member.name.fullName.toLowerCase().includes(search) ||
        member.primaryEmail.toLowerCase().includes(search)
      )
    }

    // Sort: unassigned first, then by name
    result.sort((a, b) => {
      const aAssigned = memberRolesMap.has(a.id)
      const bAssigned = memberRolesMap.has(b.id)
      if (aAssigned !== bAssigned) {
        return aAssigned ? 1 : -1
      }
      return a.name.fullName.localeCompare(b.name.fullName)
    })

    return result
  }, [assignableMembers, searchTerm, memberRolesMap])

  const handleAssign = async (memberId: string) => {
    try {
      setSaving(true)
      setError('')

      await orgChartService.assignMemberToRole(role.id, memberId)
      await onAssign()
    } catch (err: any) {
      console.error('Error assigning person:', err)
      setError(err.message || 'Failed to assign person')
      setSaving(false)
    }
  }

  const handleUnassign = async () => {
    try {
      setSaving(true)
      setError('')

      await orgChartService.assignMemberToRole(role.id, null)
      await onAssign()
    } catch (err: any) {
      console.error('Error unassigning person:', err)
      setError(err.message || 'Failed to unassign person')
      setSaving(false)
    }
  }

  if (!isOpen) return null

  const currentlyAssigned = role.assignedMember

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {currentlyAssigned ? 'Change Assignment' : 'Assign Person'}
            </h2>
            <p className="text-sm text-gray-500">{role.title}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Currently assigned */}
        {currentlyAssigned && (
          <div className="p-4 bg-purple-50 border-b border-purple-100">
            <p className="text-sm font-medium text-purple-700 mb-2">Currently Assigned</p>
            <div className="flex items-center justify-between">
              <PersonCard member={currentlyAssigned} compact />
              <button
                onClick={handleUnassign}
                disabled={saving}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
              >
                <UserMinus className="w-4 h-4" />
                <span>Leave vacant</span>
              </button>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search team members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Members list */}
        <div className="flex-1 overflow-y-auto p-4">
          {assignableMembers.length === 0 ? (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <p className="text-gray-500">No team members in directory</p>
              <p className="text-sm text-gray-400 mt-1">
                Sync from Google Directory first to load team members
              </p>
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No matches found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMembers.map(member => {
                const existingRoles = memberRolesMap.get(member.id) || []
                const isCurrentlyAssigned = member.id === currentlyAssigned?.id

                return (
                  <div
                    key={member.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      isCurrentlyAssigned 
                        ? 'border-purple-300 bg-purple-50' 
                        : 'border-gray-200 hover:bg-gray-50'
                    } transition-colors`}
                  >
                    <div className="flex-1">
                      <PersonCard member={member} compact />
                      {existingRoles.length > 0 && !isCurrentlyAssigned && (
                        <div className="ml-11 mt-1 flex flex-wrap gap-1">
                          {existingRoles.map(r => (
                            <span 
                              key={r.id}
                              className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                            >
                              {r.title}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {!isCurrentlyAssigned && (
                      <button
                        onClick={() => handleAssign(member.id)}
                        disabled={saving}
                        className="ml-4 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                      >
                        Assign
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
